import { getIsFromNodeEventSelector, getNodeDataByNodeId } from '@common-reducers/ChatbotSelectors';
import { focusNodeEventsThunk } from '@common-reducers/ChatbotThunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { TriggerSource, TriggerType, getAvailableTriggerSources, TRIGGER_SOURCE_KEY_MAP } from '@common/types/trigger.types';
import { selectEngineState } from '@common/components/connected-engines/connected-engines.selectors';
import { ErrorOutline, MoreVert, Settings } from "@mui/icons-material";
import { Box, IconButton, Modal, Tooltip, Typography } from "@mui/material";
import { Stack } from '@mui/system';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import BTChatbotModal from '../../bot-components/bt-chatbot-modal';
import NodeMenu from '../../bot-components/node-menu';
import { nodeTypes } from '../bt-nodes-def';
import classes from "./trigger-node.module.scss";
import { selectUserObject } from '@/common/reducers/UserSelectors';
import { namespace } from '@common/config/constants';

const SECTION_LABEL_SX = {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    color: 'var(--color-text-muted, var(--bulk-text-secondary))',
    textTransform: 'uppercase' as const,
    display: 'block',
    mb: 0.75,
};

const PILL_BASE_SX = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.625,
    px: 1,
    py: 0.375,
    borderRadius: '999px',
    fontSize: 11.5,
    fontWeight: 600,
    lineHeight: 1.4,
    maxWidth: '100%',
    minWidth: 0,
};

interface TriggerState {
    type: 'trigger';
    triggerType: TriggerType;
    contacts: string[];
    allowedTriggerSources?: TriggerSource[];
}

interface Props {
    id: string;
}

export default function TriggerNodeContainer({ id }: Props) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const { sessionId } = useParams<{ sessionId: string }>();
    const isFromNodeEvent = useAppSelector(state => getIsFromNodeEventSelector(state, id, sessionId));
    const isReadOnly = useAppSelector(state => state.ChatbotReducer.isReadOnly);
    const data = useAppSelector(state => getNodeDataByNodeId(state, id)) as TriggerState;

    // Get user role directly from the state slice
    // Adjust 'UserReducer' and 'role' if your state structure is different
    const userRole = useAppSelector(state => selectUserObject(state)?.role);
    // #184 A3: offer the Server Gateway source when the active engine is a gateway.
    const hasGateway = useAppSelector(state => selectEngineState(state).hasGateway);
    const availableTriggerSources = getAvailableTriggerSources(userRole ?? '', { hasGateway });

    const [menuOpen, setMenuOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const nodeType = nodeTypes?.[data?.type]

    const handleOpenNodeMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setMenuOpen((prev => !prev))
        setAnchorEl(event.currentTarget);
    };

    const handleCloseNodeMenu = () => {
        setMenuOpen(false);
        setAnchorEl(null);
    }

    const handleOpenModal = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleNodeClick = () => {
        if (isReadOnly && isFromNodeEvent) {
            dispatch(focusNodeEventsThunk(id));
        }
    };

    const selectedSources = useMemo(() => {
        const allowed = data?.allowedTriggerSources || [];
        return availableTriggerSources.filter(av => allowed.includes(av.value));
    }, [data?.allowedTriggerSources, availableTriggerSources]);

    const contactCount = data?.contacts?.length || 0;
    const isAllowList = data?.triggerType === 'allowList';
    const modeLabel = t(
        isAllowList ? 'chatbot.trigger.allowListShort' : 'chatbot.trigger.blockListShort',
        { defaultValue: isAllowList ? 'Allow list' : 'Block list' },
    );
    const contactsLabel = t('chatbot.trigger.contactsCount', {
        count: contactCount,
        defaultValue: contactCount === 1 ? '1 contact' : `${contactCount} contacts`,
    });

    const borderColor = !isFromNodeEvent && isReadOnly ? '#939393' : nodeType?.color;

    const content = (
        <Stack className={classes.trigger_container}
            sx={{
                border: `6px solid ${borderColor}`,
                cursor: (!isFromNodeEvent && isReadOnly) ? 'default' : 'pointer',
                minWidth: '220px',
                minHeight: '180px',
                padding: '12px',

                '&:hover': {
                    borderColor: `darken(${borderColor}, 10%)`,
                    boxShadow: '0 4px 8px var(--modal-shadow)'
                }
            }}
            onClick={handleNodeClick}
        >
            {/* Header Area with title and buttons */}
            <Box sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1
            }}>
                <div className={classes.trigger_title}>{t('chatbot.nodeTypes.trigger.shortTitle')}</div>

                <Box sx={{
                    display: 'flex',
                    gap: '4px'
                }}>
                    {!isReadOnly && (
                        <Tooltip title={t('chatbot.trigger.edit')} arrow placement="top">
                            <IconButton
                                size="small"
                                sx={{ color: 'var(--bulk-text-secondary)' }}
                                onClick={handleOpenModal}
                                className={classes.settings_button}
                            >
                                <Settings fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <IconButton
                        size="small"
                        sx={{ color: 'var(--bulk-text-secondary)' }}
                        onClick={handleOpenNodeMenu}
                        disabled={isReadOnly}
                    >
                        <MoreVert fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            <NodeMenu
                type={data?.type}
                open={menuOpen}
                onClose={handleCloseNodeMenu}
                anchorEl={anchorEl}
                id={id}
            />

            {/* Sources */}
            <Box sx={{ width: '100%', mt: 1.25 }}>
                <Typography sx={SECTION_LABEL_SX}>
                    {t('chatbot.trigger.sourcesLabel')}
                </Typography>
                {availableTriggerSources.length === 0 ? (
                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted, var(--bulk-text-secondary))', fontStyle: 'italic' }}>
                        {t('chatbot.trigger.noSourcesAvailableForRole')}
                    </Typography>
                ) : selectedSources.length === 0 ? (
                    <Tooltip title={t('chatbot.trigger.noSourcesSelected')} arrow placement="top">
                        <Box
                            onClick={!isReadOnly ? handleOpenModal : undefined}
                            sx={{
                                ...PILL_BASE_SX,
                                background: 'rgba(244, 67, 54, 0.10)',
                                color: '#c62828',
                                border: '1px dashed rgba(244, 67, 54, 0.45)',
                                cursor: !isReadOnly ? 'pointer' : 'default',
                            }}
                        >
                            <ErrorOutline sx={{ fontSize: 13 }} />
                            <span>{t('chatbot.trigger.noSourcesConfigured', { defaultValue: 'No source configured' })}</span>
                        </Box>
                    </Tooltip>
                ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.625 }}>
                        {selectedSources.map(source => {
                            const Icon = source.icon;
                            const sourceKey = TRIGGER_SOURCE_KEY_MAP[source.value];
                            const label = sourceKey
                                ? t(`chatbot.trigger.sources.${sourceKey}.label`, { defaultValue: source.label })
                                : source.label;
                            return (
                                <Box
                                    key={source.value}
                                    sx={{
                                        ...PILL_BASE_SX,
                                        background: 'rgba(37, 211, 102, 0.14)',
                                        color: '#1a8a47',
                                    }}
                                >
                                    <Icon style={{ fontSize: 13, width: 13, height: 13, color: '#25D366' }} />
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>

            {/* Rules */}
            <Box sx={{ width: '100%', mt: 1.5 }}>
                <Typography sx={SECTION_LABEL_SX}>
                    {t('chatbot.trigger.contactRules')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.625, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text)' }}>
                        {modeLabel}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted, var(--bulk-text-secondary))' }}>
                        · {contactsLabel}
                    </Typography>
                </Box>
            </Box>

            {/* Edit Modal */}
            <Modal
                id={modalOpen ? 'trigger-edit-modal' : undefined}
                open={modalOpen}
                onClose={handleCloseModal}
                sx={{
                    margin: "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <BTChatbotModal type="setTrigger" onClose={handleCloseModal} id={id} />
            </Modal>
        </Stack>
    );

    if (isReadOnly && isFromNodeEvent) {
        return (
            <Tooltip title={t('chatbot.timelineTooltip')} arrow placement="top">
                {content}
            </Tooltip>
        );
    }

    return content;
} 
